<template>
  <b-overlay :show="loading" rounded="sm" no-fade class="mt-1">
    <b-card>
      <b-row class="mb-1">
        <table class="table table-sm">
          <thead>
            <tr>
              <th scope="col" v-if="siteId > 0">Site Edit</th>
              <th scope="col" v-else>Site Add</th>
            </tr>
          </thead>
        </table>
      </b-row>

      <b-form>
        <b-row>
          <b-col cols="12">
            <b-form-group label="Department" label-for="department">
              <b-form-select id="department" v-model="siteValue.department_id" :options="departments" required></b-form-select>
            </b-form-group>
          </b-col>

          <b-col cols="12">
            <b-form-group label="Site" label-for="title">
              <b-form-input required id="title" type="text" v-model="siteValue.title"></b-form-input>
            </b-form-group>
          </b-col>

          <b-col cols="3">
            <b-form-group label="Start AM Time" label-for="start_am_time">
              <flat-pickr v-model="siteValue.start_am_time" :config="startAmPickerConfig" class="form-control" required></flat-pickr>
            </b-form-group>
          </b-col>
          <b-col cols="3">
            <b-form-group label="End AM Time" label-for="end_am_time">
              <flat-pickr v-model="siteValue.end_am_time" :config="endAmPickerConfig" class="form-control" required></flat-pickr>
            </b-form-group>
          </b-col>
          <b-col cols="3">
            <b-form-group label="Start PM Time" label-for="start_pm_time">
              <flat-pickr v-model="siteValue.start_pm_time" :config="startPmPickerConfig" class="form-control" required></flat-pickr>
            </b-form-group>
          </b-col>
          <b-col cols="3">
            <b-form-group label="End PM Time" label-for="end_pm_time">
              <flat-pickr v-model="siteValue.end_pm_time" :config="endPmPickerConfig" class="form-control" required></flat-pickr>
            </b-form-group>
          </b-col>

          <b-col cols="12">
            <b-form-group label="Homepage Default Number of Weeks" label-for="weeks">
              <b-form-input required id="weeks" type="number" v-model="siteValue.default_home_weeks"></b-form-input>
            </b-form-group>
          </b-col>

        </b-row>

        
        <!-- Site Offline Management Section (only show when editing) -->
        <div v-if="siteId > 0">
          <hr />
          <h5>Site Offline Management</h5>
          
          <b-row class="mb-3">
            <b-col cols="12">
              <b-button @click="showOfflineModal = true" variant="warning" class="mr-2">
                <feather-icon icon="PowerIcon" class="mr-50" size="16" />
                Take Site Offline
              </b-button>
              <b-button @click="restoreSiteOnline" variant="success" class="mr-2">
                <feather-icon icon="PowerIcon" class="mr-50" size="16" />
                Restore Site Online
              </b-button>
             
            </b-col>
          </b-row>

          <!-- Offline Periods History -->
          <b-row v-if="offlinePeriods.length > 0" class="mb-3">
            <b-col cols="12">
              <h6>Offline Periods History</h6>
              <b-table
                :items="offlinePeriods"
                :fields="offlineTableFields"
                responsive
                small
                striped
              >
                <template #cell(offline_start)="row">
                  {{ formatDateTime(row.item.offline_start) }}
                </template>
                <template #cell(offline_end)="row">
                  {{ formatDateTime(row.item.offline_end) }}
                </template>
                <template #cell(status)="row">
                  <b-badge :variant="isOfflinePeriodActive(row.item) ? 'danger' : 'secondary'">
                    {{ isOfflinePeriodActive(row.item) ? 'Offline' : 'Completed' }}
                  </b-badge>
                </template>
              </b-table>
            </b-col>
          </b-row>
        </div>


        <hr />

        <b-row>
          <b-col cols="12">
            <b-button v-if="siteId == null" type="submit" @click.prevent="formSubmitted" variant="primary" class="float-right">
              Save
            </b-button>
            <b-button v-else type="submit" @click.prevent="formSubmitted" variant="primary" class="float-right">
              Save Changes
            </b-button>
          </b-col>
        </b-row>
      </b-form>
    </b-card>
     <!-- Take Site Offline Modal -->
    <b-modal
      v-model="showOfflineModal"
      title="Take Site Offline"
      @ok="takeSiteOffline"
      ok-title="Take Offline"
      ok-variant="warning"
      cancel-title="Cancel"
      size="lg"
    >
      <b-form>
        <b-row>
          <b-col cols="6">
            <b-form-group label="Offline Start Date & Time" label-for="offline_start">
              <flat-pickr 
                v-model="offlineForm.offline_start" 
                :config="offlineDatePickerConfig" 
                class="form-control" 
                required
              ></flat-pickr>
            </b-form-group>
          </b-col>
          <b-col cols="6">
            <b-form-group label="Offline End Date & Time" label-for="offline_end">
              <flat-pickr 
                v-model="offlineForm.offline_end" 
                :config="offlineDatePickerConfig" 
                class="form-control" 
                required
              ></flat-pickr>
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="12">
            <b-form-group label="Reason (Optional)" label-for="reason">
              <b-form-textarea
                id="reason"
                v-model="offlineForm.reason"
                placeholder="Enter reason for taking site offline..."
                rows="3"
              ></b-form-textarea>
            </b-form-group>
          </b-col>
        </b-row>
        <b-alert show variant="warning" class="mt-2">
          <strong>Warning:</strong> Taking the site offline will delete all schedules within the specified period.
        </b-alert>
      </b-form>
    </b-modal>

  </b-overlay>
</template>
<script>
import store from '@/store';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import { onUnmounted } from '@vue/composition-api';
import settingsStoreModule from '../settingsStoreModule';
import vSelect from 'vue-select';
import router from '@/router';
import AppColorPaletteVue from '@/@core/layouts/components/AppColorPalette.vue';
import flatPickr from 'vue-flatpickr-component';
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue';
import { INVALID_TIME_RANGE_MESSAGE, isValidTimeRange, bumpEndTime, apiErrorMessage } from '@/libs/timeRange';

export default {
  components: {
    vSelect,
    ToastificationContent,
    AppColorPaletteVue,
    flatPickr,
    FeatherIcon,
  },

  setup() {
    const SITE_APP_STORE_MODULE_NAME = 'settings';
    // Register module
    if (!store.hasModule(SITE_APP_STORE_MODULE_NAME)) store.registerModule(SITE_APP_STORE_MODULE_NAME, settingsStoreModule);

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(SITE_APP_STORE_MODULE_NAME)) store.unregisterModule(SITE_APP_STORE_MODULE_NAME);
    });
  },

  data() {
    return {
      siteValue: {
        title: '',
        department_id: '',
        start_am_time: '08:00',
        end_am_time: '12:00',
        start_pm_time: '13:00',
        end_pm_time: '17:00',
        default_home_weeks:null,
      },
      startAmPickerConfig: {
        enableTime: true,
        noCalendar: true,
        dateFormat: 'H:i',
        time_24hr: true,
      },
      endAmPickerConfig: {
        enableTime: true,
        noCalendar: true,
        dateFormat: 'H:i',
        time_24hr: true,
      },
      startPmPickerConfig: {
        enableTime: true,
        noCalendar: true,
        dateFormat: 'H:i',
        time_24hr: true,
      },
      endPmPickerConfig: {
        enableTime: true,
        noCalendar: true,
        dateFormat: 'H:i',
        time_24hr: true,
      },
       offlineDatePickerConfig: {
        enableTime: true,
        dateFormat: 'Y-m-d',
        time_24hr: true,
      },

      departments: [],
      loading: false,
      siteId: null,
      selectedDepartment: null,
       showOfflineModal: false,
      offlineForm: {
        offline_start: '',
        offline_end: '',
        reason: '',
      },
      offlinePeriods: [],
      offlineTableFields: [
        { key: 'offline_start', label: 'Start Date', sortable: true },
        { key: 'offline_end', label: 'End Date', sortable: true },
        { key: 'reason', label: 'Reason' },
        { key: 'status', label: 'Status' },
      ],
    };
  },

  watch: {},

  mounted() {
    this.getSiteById();
  },

  watch: {
    'siteValue.start_am_time'(newTime) {
      if (newTime) {
        this.endAmPickerConfig.minTime = newTime;
        this.siteValue.end_am_time = bumpEndTime(newTime, this.siteValue.end_am_time);
      }
    },
    'siteValue.start_pm_time'(newTime) {
      if (newTime) {
        this.endPmPickerConfig.minTime = newTime;
        this.siteValue.end_pm_time = bumpEndTime(newTime, this.siteValue.end_pm_time);
      }
    },
  },

  methods: {
    getSiteById() {
      this.loading = true;

      if (router.currentRoute.params.id) {
        this.siteId = parseInt(router.currentRoute.params.id);

        store
          .dispatch('settings/getSiteById', { id: router.currentRoute.params.id })
          .then((res) => {
            this.siteValue = res.data;
            this.loadOfflinePeriods();

          })
          .catch((error) => {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'An error occurred',
                text: 'Please try again later or contact support.',
                icon: 'AlertCircleIcon',
                variant: 'danger',
              },
            });
          });
      }
      this.getDepartments();
    },

    getDepartments() {
      store
        .dispatch('settings/getAllDepartments')
        .then((res) => {
          this.departments = res.data.map((department) => ({
            value: department.id,
            text: department.title,
          }));

          this.loading = false;
        })
        .catch((error) => {
          this.$router.push({ name: 'settings' });
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'An error occurred',
              text: 'Please try again later or contact support.',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          });
        });
    },

    formSubmitted() {
      this.loading = true;
      if (!this.siteValue.title || !this.siteValue.department_id) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: 'Please fill in all fields.',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        });
        this.loading = false;
        return;
      }

      if (
        !isValidTimeRange(this.siteValue.start_am_time, this.siteValue.end_am_time) ||
        !isValidTimeRange(this.siteValue.start_pm_time, this.siteValue.end_pm_time)
      ) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: INVALID_TIME_RANGE_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        });
        this.loading = false;
        return;
      }

      if (this.siteId == null) {
        store
          .dispatch('settings/saveSite', this.siteValue)
          .then((response) => {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Successful',
                text: '✔️ Site Add Successful',
                icon: 'ThumbsUpIcon',
                variant: 'success',
              },
            });

            router.push({ name: 'settings-sites' });
          })
          .catch((error) => {
            this.loading = false;
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'An error occurred',
                text: apiErrorMessage(error, 'Please try again later or contact support.'),
                icon: 'AlertCircleIcon',
                variant: 'danger',
              },
            });
          });
      } else {
        store
          .dispatch('settings/updateSite', this.siteValue)
          .then((response) => {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Successful',
                text: '✔️ Site Update Successful',
                icon: 'ThumbsUpIcon',
                variant: 'success',
              },
            });

            router.push({ name: 'settings-sites' });
          })
          .catch((error) => {
            this.loading = false;
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'An error occurred',
                text: apiErrorMessage(error, 'Please try again later or contact support.'),
                icon: 'AlertCircleIcon',
                variant: 'danger',
              },
            });
          });
      }
    },
    
    takeSiteOffline() {
      if (!this.offlineForm.offline_start || !this.offlineForm.offline_end) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Validation Error',
            text: 'Please fill in both start and end dates.',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        });
        return;
      }

      if (new Date(this.offlineForm.offline_start) >= new Date(this.offlineForm.offline_end)) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Validation Error',
            text: 'End date must be after start date.',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        });
        return;
      }

      this.loading = true;

      const payload = {
        site_id: this.siteId,
        offline_start: this.offlineForm.offline_start,
        offline_end: this.offlineForm.offline_end,
        reason: this.offlineForm.reason,
      };

      store
        .dispatch('settings/takeSiteOffline', payload)
        .then((response) => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Success',
              text: 'Site has been taken offline successfully.',
              icon: 'CheckIcon',
              variant: 'success',
            },
          });
          
          // Reset form and close modal
          this.offlineForm = {
            offline_start: '',
            offline_end: '',
            reason: '',
          };
          this.showOfflineModal = false;
          
          // Reload offline periods
          this.loadOfflinePeriods();
        })
        .catch((error) => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error',
              text: error.response?.data?.message || 'Failed to take site offline.',
              icon: 'AlertCircleIcon',
              variant: 'danger',
            },
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },

    restoreSiteOnline() {
      this.loading = true;

      const payload = {
        site_id: this.siteId,
      };

      store
        .dispatch('settings/restoreSiteOnline', payload)
        .then((response) => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Success',
              text: 'Site has been restored online successfully.',
              icon: 'CheckIcon',
              variant: 'success',
            },
          });
          
          // Reload offline periods
          this.loadOfflinePeriods();
        })
        .catch((error) => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error',
              text: error.response?.data?.message || 'Failed to restore site online.',
              icon: 'AlertCircleIcon',
              variant: 'danger',
            },
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },

    loadOfflinePeriods() {
      if (!this.siteId) return;

      store
        .dispatch('settings/getOfflinePeriods', this.siteId)
        .then((response) => {
          this.offlinePeriods = response.data || [];
        })
        .catch((error) => {
          console.error('Failed to load offline periods:', error);
        });
    },

    formatDateTime(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },

    isOfflinePeriodActive(period) {
      const now = new Date();
      const start = new Date(period.offline_start);
      const end = new Date(period.offline_end);
      return now >= start && now <= end;
    },

  },
};
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/vue-flatpicker.scss';

.mr-50 {
  margin-right: 0.5rem;
}
</style>
